import { useEffect, useRef, useState } from "react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { ChevronDown, FileSpreadsheet, Printer, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../api";
import { PageHead, Filters, Kpi, Panel, LoadingBlock, InsightSpark } from "../shell";
import { money, pct, growthClass, achievementSeverity } from "../lib/format";

export default function Overview({ dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadedQueryKey, setLoadedQueryKey] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const requestSequence = useRef(0);
  const queryKey = JSON.stringify(periodParams);

  useEffect(() => {
    const requestId = ++requestSequence.current;
    const controller = new AbortController();
    setData(null);
    setInsights(null);
    setLoadedQueryKey(null);
    api.get("/dashboard/summary", { params: periodParams, signal: controller.signal }).then((response) => {
      if (requestSequence.current !== requestId) return;
      setData(response.data);
      setLoadedQueryKey(queryKey);
    }).catch((error) => {
      if (error.code !== "ERR_CANCELED") console.error("Overview summary could not be loaded", error);
    });
    api.get("/analytics/insights", { params: periodParams, signal: controller.signal }).then((response) => {
      if (requestSequence.current === requestId) setInsights(response.data);
    }).catch((error) => {
      if (error.code !== "ERR_CANCELED") console.error("Overview insights could not be loaded", error);
    });
    return () => controller.abort();
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || loadedQueryKey !== queryKey) return <main className="content"><PageHead eyebrow="YÖNETİM ÖZETİ" title="Genel Bakış" desc="Şirketinizin performansını tek bakışta izleyin." />
    <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
             startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
             isDemo={dataset?.state?.active === "DEMO"} />
    <LoadingBlock label="Yönetim verileri hazırlanıyor..." /></main>;

  const k = data.kpis;
  const growthCls = (n) => growthClass(n);
  const showCumMonths = data.monthly.filter((m) => m.in_period);
  const downloadExcel = async () => {
    setExporting(true); setExportError(""); setExportMenuOpen(false);
    try {
      const response = await api.get("/dashboard/export/excel", { params: periodParams, responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const filename = disposition.match(/filename="?([^";]+)"?/)?.[1] || "genel-bakis.xlsx";
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a"); link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (error) {
      let message = "Excel raporu indirilemedi. Lütfen bağlantınızı kontrol edip tekrar deneyin.";
      if (error.response?.data instanceof Blob) {
        try { const body = JSON.parse(await error.response.data.text()); message = body.detail || message; } catch (_) {}
      } else if (error.response?.data?.detail) message = error.response.data.detail;
      setExportError(message);
    } finally { setExporting(false); }
  };
  const printDashboard = () => { setExportMenuOpen(false); setExportError(""); window.print(); };

  return (
    <main className="content overview-page">
      <PageHead eyebrow={<span data-testid="overview-period-heading">YÖNETİM ÖZETİ · {data.period}</span>} title="Genel Bakış"
        desc="Şirketinizin performansını tek bakışta izleyin."
        actions={<div className="overview-export no-print">
          <button data-testid="export-report-button" className="outline-button" disabled={exporting} onClick={() => setExportMenuOpen(!exportMenuOpen)}><FileSpreadsheet size={16}/>{exporting ? "Hazırlanıyor..." : "Raporu dışa aktar"}<ChevronDown size={13}/></button>
          {exportMenuOpen && <div className="overview-export-menu" data-testid="overview-export-menu">
            <button data-testid="export-overview-excel-button" onClick={downloadExcel}><FileSpreadsheet size={15}/><span><strong>Excel (.xlsx)</strong><small>KPI, bölge ve kapsam verileri</small></span></button>
            <button data-testid="print-overview-pdf-button" onClick={printDashboard}><Printer size={15}/><span><strong>PDF olarak kaydet</strong><small>Mevcut dashboard görünümü</small></span></button>
          </div>}
        </div>} />
      <section className="overview-print-meta print-only" data-testid="overview-print-meta">
        <div><span>Rapor Dönemi</span><strong>{data.period}</strong></div>
        <div><span>Kesim Tarihi</span><strong>{data.report_cutoff.split("-").reverse().join(".")}</strong></div>
        <div><span>Kullanıcı Kapsamı</span><strong>{data.user_scope?.label || "—"}</strong></div>
      </section>
      {exportError && <div data-testid="overview-export-error" role="alert" className="error no-print">{exportError}</div>}
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={data.is_demo} />

      <section className="kpi-grid">
        <Kpi testid="kpi-net-premium" label="Net üretim" value={money(k.premium, true)}
          change={k.premium_growth !== null ? `${pct(k.premium_growth, 1)} YoY` : "YoY verisi yok"}
          changeType={growthCls(k.premium_growth)} sub={money(k.premium)} />
        <Kpi testid="kpi-commission" label="Komisyon" value={money(k.commission, true)}
          change={k.commission_growth !== null ? `${pct(k.commission_growth, 1)} YoY` : "YoY verisi yok"}
          changeType={growthCls(k.commission_growth)} sub={money(k.commission)} />
        <Kpi testid="kpi-target-achievement" label="Hedef gerçekleşme" value={pct(k.achievement, 1)}
          change={k.achievement >= 100 ? "Hedef üzerinde" : `Hedefin ${(100 - k.achievement).toFixed(1).replace(".", ",")} puan altında`}
          changeType={k.achievement >= 100 ? "positive" : k.achievement >= 90 ? "neutral" : "negative"}
          sub={`Dönem hedefi ${money(k.target, true)}`} />
        <Kpi testid="kpi-forecast" label="Yıl sonu tahmini" value={money(k.forecast_commission, true)}
          change={`Hedef ${pct(k.forecast_achievement, 1)}`}
          changeType={k.forecast_achievement >= 100 ? "positive" : k.forecast_achievement >= 90 ? "neutral" : "negative"}
          sub={`${k.forecast_method} · Kesim ${data.report_cutoff.split("-").reverse().join(".")}`} />
        <Kpi testid="kpi-effective-rate" label="Efektif komisyon" value={pct(k.effective_rate, 2)}
          change={`GY %${(k.prev_effective_rate || 0).toFixed(2).replace(".", ",")}`}
          changeType={k.effective_rate >= k.prev_effective_rate ? "positive" : "negative"}
          sub="Komisyon / net prim" />
      </section>
      <section className="kpi-grid">
        <Kpi testid="kpi-policy-count" label="Poliçe adedi" value={new Intl.NumberFormat("tr-TR").format(k.policies)}
          sub="Tekil ana poliçe (zeyil hariç)" />
        <Kpi testid="kpi-movement-count" label="Dönem içi hareket adedi" value={new Intl.NumberFormat("tr-TR").format(k.total_movements)}
          sub={`Kaynak hareketleri · Zeyil ${new Intl.NumberFormat("tr-TR").format(k.endorsement_count)} · İptal dahil`} />
        <Kpi testid="kpi-previous-premium" label="GY net üretim" value={money(k.previous_premium, true)}
          sub="Geçen yıl aynı dönem" />
        <Kpi testid="kpi-previous-commission" label="GY komisyon" value={money(k.previous_commission, true)}
          sub="Geçen yıl aynı dönem" />
        <Kpi testid="kpi-remaining-target" label="Kalan yıllık hedef"
          value={money(k.remaining_annual_target, true)}
          sub={`Yıl sonuna ${12 - data.period_end_month} ay`} />
        <Kpi testid="kpi-avg-policy" label="Ortalama prim / poliçe"
          value={money(k.policies ? k.premium / k.policies : 0, true)}
          sub={`${new Intl.NumberFormat("tr-TR").format(k.policies)} tekil poliçe`} />
      </section>

      <section className="chart-grid">
        <div className="panel chart-panel wide">
          <div className="panel-head">
            <div><h3>Aylık net üretim</h3><span>Mevcut dönem ve önceki yıl karşılaştırması</span></div>
            <div className="legend"><i className="blue"/> {data.year} <i className="gray"/> {data.year - 1}</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.monthly}>
              <defs>
                <linearGradient id="fillBlue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2d6cdf" stopOpacity={0.22}/>
                  <stop offset="95%" stopColor="#2d6cdf" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf4" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }}
                tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="premium" stroke="#2d6cdf" strokeWidth={2.5} fill="url(#fillBlue)" />
              <Area type="monotone" dataKey="previous" stroke="#b7c1ce" strokeDasharray="5 5" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="panel chart-panel">
          <div className="panel-head">
            <div><h3>Aylık komisyon ve hedef</h3><span>Gerçekleşen · geçen yıl · hedef</span></div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf4" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }}
                tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
              <Tooltip formatter={(v) => money(v)} />
              <Line dataKey="commission" stroke="#164a43" strokeWidth={2.5} dot={false} name="Komisyon" />
              <Line dataKey="previous_commission" stroke="#b7c1ce" strokeDasharray="4 4" strokeWidth={2} dot={false} name="GY Komisyon" />
              <Line dataKey="target" stroke="#c3943f" strokeDasharray="6 3" strokeWidth={2} dot={false} name="Hedef" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="chart-grid">
        <div className="panel chart-panel wide">
          <div className="panel-head">
            <div><h3>Kümülatif komisyon vs hedef</h3><span>Yıl içi hedef takibi</span></div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.monthly}>
              <defs>
                <linearGradient id="fillGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#c3943f" stopOpacity={0.18}/>
                  <stop offset="95%" stopColor="#c3943f" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf4" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8995a7" }}
                tickFormatter={(v) => `${(v/1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="cumulative_target" stroke="#c3943f" strokeWidth={2.5} fill="url(#fillGold)" name="Kümülatif hedef" />
              <Line type="monotone" dataKey="cumulative_commission" stroke="#164a43" strokeWidth={2.5} dot={false} name="Kümülatif komisyon" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="panel chart-panel">
          <div className="panel-head"><div><h3>Poliçe grubu dağılımı</h3><span>Net üretim bazında</span></div></div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.groups} layout="vertical" margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e8edf4" />
              <XAxis type="number" hide />
              <YAxis dataKey="code" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#526174" }} width={44} />
              <Tooltip formatter={(v, k, p) => [money(v), p.payload.name]} />
              <Bar dataKey="premium" fill="#2d6cdf" radius={[0, 3, 3, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="lower-grid">
        <Panel title="Bölge performansı" sub="Net üretim ve hedef görünümü"
          actions={<a data-testid="view-regions-link" className="text-button" href="/regions">Tümünü gör</a>}>
          <table data-testid="region-performance-table">
            <thead><tr><th>Bölge</th><th>Net üretim</th><th>Komisyon</th><th>Hedef %</th><th>Büyüme</th></tr></thead>
            <tbody>
              {data.regions.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong></td>
                  <td>{money(r.premium, true)}</td>
                  <td>{money(r.commission, true)}</td>
                  <td><span className={"pill " + achievementSeverity(r.achievement)}>{pct(r.achievement)}</span></td>
                  <td className={growthCls(r.growth)}>{r.growth !== null ? pct(r.growth) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title="Yönetici içgörüleri" sub="Veriden üretilen kısa notlar" actions={<InsightSpark />} className="insights">
          {!insights && <div className="muted" style={{ fontSize: 11 }}>Hesaplanıyor...</div>}
          {insights?.items?.length === 0 && <div className="muted" style={{ fontSize: 11 }}>Şu an dikkat çeken bir bulgu yok.</div>}
          {insights?.items?.slice(0, 6).map((x, i) => (
            <div data-testid={`insight-${i + 1}`} className={"insight sev-" + x.severity} key={x.key}>
              <span>0{i + 1}</span><p>{x.text}</p>
              {x.severity === "positive" ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
            </div>
          ))}
          {insights && insights.renewal_coverage_pct < 60 && (
            <div className="warn-banner" data-testid="renewal-coverage-warn">
              Nihai Yeni İş/Yenileme sınıfı yalnız %{insights.renewal_coverage_pct} kayıt için belirli — sınıflandırma raporlarını inceleyin.
            </div>
          )}
        </Panel>
      </section>

      <section className="lower-grid">
        <Panel title="En yüksek üretim yapan 10 satıcı" sub="Net üretim bazında">
          <table data-testid="top-sellers-premium-table">
            <thead><tr><th>Satıcı</th><th>Bölge</th><th>Net üretim</th><th>Komisyon</th></tr></thead>
            <tbody>
              {data.top_sellers_premium.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td><td>{s.region}</td>
                  <td>{money(s.premium, true)}</td><td>{money(s.commission, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title="En yüksek komisyon üreten 10 satıcı" sub="Komisyon bazında">
          <table data-testid="top-sellers-commission-table">
            <thead><tr><th>Satıcı</th><th>Bölge</th><th>Komisyon</th><th>Poliçe</th></tr></thead>
            <tbody>
              {data.top_sellers_commission.map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td><td>{s.region}</td>
                  <td>{money(s.commission, true)}</td><td>{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </section>
    </main>
  );
}
